import {issueHovercardPath} from '@github-ui/paths'

import {
  type BaseHovercardOptions,
  type HovercardAttributesResult,
  buildHovercardAttributes,
  useHovercardAttributesBase,
} from './shared'

export type IssueHovercardOptions = BaseHovercardOptions &
  (
    | {
        owner: string | undefined
        repo: string | undefined
        issueNumber: number | undefined
        url?: never
      }
    | {
        /** URL override - use when the API provides a pre-computed hovercard URL */
        url: string
        owner?: never
        repo?: never
        issueNumber?: never
      }
  )

/**
 * Generate hovercard attributes for an issue.
 */
function issueHovercardAttributes(options: IssueHovercardOptions): HovercardAttributesResult {
  let url: string

  if ('url' in options && options.url) {
    url = options.url
  } else if ('owner' in options && options.owner && options.repo && options.issueNumber != null) {
    url = issueHovercardPath({owner: options.owner, repo: options.repo, issueNumber: options.issueNumber})
  } else {
    return {}
  }

  return buildHovercardAttributes(url, 'issue', options.tracking ?? true)
}

/**
 * React hook for issue hovercard attributes.
 *
 * @example
 * ```tsx
 * function IssueLink({ owner, repo, issueNumber }: Props) {
 *   const hovercardAttrs = useHovercardAttributesForIssue({owner, repo, issueNumber})
 *   return <a href={`/${owner}/${repo}/issues/${issueNumber}`} {...hovercardAttrs}>#{issueNumber}</a>
 * }
 * ```
 */
export function useHovercardAttributesForIssue(options: IssueHovercardOptions): HovercardAttributesResult {
  return useHovercardAttributesBase(issueHovercardAttributes(options))
}
